// lib/drawing-layer.ts
import type { DrawingLayerPayload, DrawingTextAlign, DrawingToolKind, DrawingToolStyle, Rect } from '#types/types';
import {
  escapeSvgText,
  escapeXmlAttribute,
  getSvgLayerPayload,
  svgMarkupToDataUrl,
} from '#lib/svg-layer';

export const DRAWING_LAYER_PARAM_KEY = '__faterDrawingLayer';
export const DEFAULT_DRAWING_TEXT_FONT_FAMILY = 'ui-sans-serif, -apple-system, system-ui, sans-serif';

const MIN_SHAPE_SIZE = 4;
const TEXT_PADDING_FACTOR = 0.28;
const DEFAULT_TEXT_LINE_HEIGHT = 1.22;

export interface ShapeDrawingInput {
  kind: Exclude<DrawingToolKind, 'text' | 'pencil' | 'polygon'>;
  start: { x: number; y: number };
  end: { x: number; y: number };
  style: DrawingToolStyle;
}

export interface PencilDrawingInput {
  points: Array<{ x: number; y: number }>;
  style: DrawingToolStyle;
}

export interface PolygonDrawingInput {
  points: Array<{ x: number; y: number }>;
  style: DrawingToolStyle;
}

export interface TextDrawingInput {
  text: string;
  anchor: { x: number; y: number };
  style: DrawingToolStyle;
}

export interface DrawingSvgResult {
  svg: string;
  worldRect: Rect;
  payload: DrawingLayerPayload;
}

export interface DrawingLayerSvgSource {
  svg: string;
  source: string;
  width: number;
  height: number;
  viewBox: string;
}

const clampFinite = (value: number, min: number, max: number): number => {
  if (!Number.isFinite(value)) return min;
  return Math.min(max, Math.max(min, value));
};

const normalizeHexColor = (value: unknown, fallback: string): string => {
  const trimmed = typeof value === 'string' ? value.trim() : '';
  return /^#[0-9a-f]{6}$/i.test(trimmed) ? trimmed : fallback;
};

const normalizeOpacity = (value: unknown): number => (
  clampFinite(typeof value === 'number' ? value : 1, 0, 1)
);

const formatSvgNumber = (value: number): string => (
  Number.isFinite(value) ? String(Number(value.toFixed(3))) : '0'
);

const normalizeFontFamily = (value: unknown): string => {
  if (typeof value !== 'string') return DEFAULT_DRAWING_TEXT_FONT_FAMILY;
  const trimmed = value.trim();
  if (!trimmed || trimmed.length > 160 || /[<>{};]/.test(trimmed)) {
    return DEFAULT_DRAWING_TEXT_FONT_FAMILY;
  }
  return trimmed;
};

const normalizeTextAlign = (value: unknown): DrawingTextAlign => (
  value === 'center' || value === 'right' ? value : 'left'
);

const normalizeFontWeight = (style: DrawingToolStyle): number => {
  const rawWeight = typeof style.fontWeight === 'number' && Number.isFinite(style.fontWeight)
    ? style.fontWeight
    : (style.textBold === true ? 700 : 400);
  return Math.round(clampFinite(rawWeight, 100, 900));
};

export const normalizeDrawingStyle = (style: DrawingToolStyle): DrawingToolStyle => {
  const fontWeight = normalizeFontWeight(style);

  return {
    strokeColor: normalizeHexColor(style.strokeColor, '#B3D9FF'),
    strokeOpacity: normalizeOpacity(style.strokeOpacity),
    fillColor: normalizeHexColor(style.fillColor, '#3B82F6'),
    fillOpacity: normalizeOpacity(style.fillOpacity),
    fillEnabled: style.fillEnabled === true,
    strokeWidth: Math.round(clampFinite(style.strokeWidth, 0, 256)),
    cornerRadius: Math.round(clampFinite(style.cornerRadius, 0, 2048)),
    fontSize: Math.round(clampFinite(style.fontSize, 8, 1024)),
    fontColor: normalizeHexColor(style.fontColor, '#FFFFFF'),
    fontOpacity: normalizeOpacity(style.fontOpacity),
    textBold: fontWeight >= 700,
    textItalic: style.textItalic === true,
    textUnderline: style.textUnderline === true,
    fontFamily: normalizeFontFamily(style.fontFamily),
    fontWeight,
    lineHeight: clampFinite(style.lineHeight ?? DEFAULT_TEXT_LINE_HEIGHT, 0.7, 4),
    letterSpacingPercent: clampFinite(style.letterSpacingPercent ?? 0, -100, 500),
    textAlign: normalizeTextAlign(style.textAlign),
  };
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const readFiniteNumber = (value: unknown, fallback: number): number => (
  typeof value === 'number' && Number.isFinite(value) ? value : fallback
);

const readDrawingRect = (value: unknown): Rect => {
  if (!isRecord(value)) return { x: 0, y: 0, w: 1, h: 1 };
  return {
    x: readFiniteNumber(value.x, 0),
    y: readFiniteNumber(value.y, 0),
    w: Math.max(1, readFiniteNumber(value.w, 1)),
    h: Math.max(1, readFiniteNumber(value.h, 1)),
  };
};

const readDrawingPoints = (value: unknown): Array<{ x: number; y: number }> | undefined => {
  if (!Array.isArray(value)) return undefined;
  const points = value
    .map((point) => {
      if (!isRecord(point)) return null;
      const x = readFiniteNumber(point.x, Number.NaN);
      const y = readFiniteNumber(point.y, Number.NaN);
      if (!Number.isFinite(x) || !Number.isFinite(y)) return null;
      return { x, y };
    })
    .filter((point): point is { x: number; y: number } => point !== null);
  return points.length > 0 ? points : undefined;
};

type PolygonalShapeKind = 'triangle' | 'diamond' | 'star' | 'pentagon' | 'hexagon';

const isPolygonalShapeKind = (kind: DrawingToolKind): kind is PolygonalShapeKind => (
  kind === 'triangle' ||
  kind === 'diamond' ||
  kind === 'star' ||
  kind === 'pentagon' ||
  kind === 'hexagon'
);

const getPolygonalShapePoints = (
  kind: PolygonalShapeKind,
  width: number,
  height: number,
  inset: number,
): Array<{ x: number; y: number }> => {
  const safeInset = Math.max(0, Math.min(inset, width / 2, height / 2));
  const left = safeInset;
  const top = safeInset;
  const right = Math.max(left + 1, width - safeInset);
  const bottom = Math.max(top + 1, height - safeInset);
  const centerX = width / 2;
  const centerY = height / 2;

  if (kind === 'triangle') {
    return [
      { x: centerX, y: top },
      { x: right, y: bottom },
      { x: left, y: bottom },
    ];
  }

  if (kind === 'diamond') {
    return [
      { x: centerX, y: top },
      { x: right, y: centerY },
      { x: centerX, y: bottom },
      { x: left, y: centerY },
    ];
  }

  const radiusX = Math.max(1, (right - left) / 2);
  const radiusY = Math.max(1, (bottom - top) / 2);

  if (kind === 'star') {
    const innerRadiusX = Math.max(1, radiusX * 0.45);
    const innerRadiusY = Math.max(1, radiusY * 0.45);
    return Array.from({ length: 10 }, (_, index) => {
      const isOuter = index % 2 === 0;
      const angle = -Math.PI / 2 + index * (Math.PI / 5);
      return {
        x: centerX + Math.cos(angle) * (isOuter ? radiusX : innerRadiusX),
        y: centerY + Math.sin(angle) * (isOuter ? radiusY : innerRadiusY),
      };
    });
  }

  const sideCount = kind === 'pentagon' ? 5 : 6;
  return Array.from({ length: sideCount }, (_, index) => {
    const angle = -Math.PI / 2 + index * ((Math.PI * 2) / sideCount);
    return {
      x: centerX + Math.cos(angle) * radiusX,
      y: centerY + Math.sin(angle) * radiusY,
    };
  });
};

export const getDrawingLayerPayload = (params: unknown): DrawingLayerPayload | null => {
  if (!isRecord(params)) return null;
  const payload = params[DRAWING_LAYER_PARAM_KEY];
  if (!isRecord(payload)) return null;
  const kind = payload.kind;
  if (
    kind !== 'rectangle' &&
    kind !== 'ellipse' &&
    kind !== 'triangle' &&
    kind !== 'diamond' &&
    kind !== 'star' &&
    kind !== 'pentagon' &&
    kind !== 'hexagon' &&
    kind !== 'line' &&
    kind !== 'arrow' &&
    kind !== 'pencil' &&
    kind !== 'text' &&
    kind !== 'polygon'
  ) {
    return null;
  }
  const style = isRecord(payload.style)
    ? normalizeDrawingStyle(payload.style as unknown as DrawingToolStyle)
    : normalizeDrawingStyle({
        strokeColor: '#B3D9FF',
        fillColor: '#3B82F6',
        fillEnabled: false,
        strokeWidth: 4,
        cornerRadius: 16,
        fontSize: 48,
        fontColor: '#FFFFFF',
        textBold: false,
        textItalic: false,
        textUnderline: false,
      });

  return {
    kind,
    style,
    text: typeof payload.text === 'string' ? payload.text : undefined,
    points: readDrawingPoints(payload.points),
    createdAt: readFiniteNumber(payload.createdAt, Date.now()),
    worldRect: readDrawingRect(payload.worldRect),
  };
};

const resolveShapeDrawingWorldRect = (
  start: { x: number; y: number },
  end: { x: number; y: number },
  kind: Exclude<DrawingToolKind, 'text' | 'pencil' | 'polygon'>,
  style: DrawingToolStyle,
): Rect => {
  const normalizedStyle = normalizeDrawingStyle(style);
  const minX = Math.min(start.x, end.x);
  const minY = Math.min(start.y, end.y);
  const maxX = Math.max(start.x, end.x);
  const maxY = Math.max(start.y, end.y);
  const width = Math.max(MIN_SHAPE_SIZE, maxX - minX);
  const height = Math.max(MIN_SHAPE_SIZE, maxY - minY);

  if (kind === 'line' || kind === 'arrow') {
    const padding = Math.max(8, normalizedStyle.strokeWidth * 3);
    return {
      x: minX - padding,
      y: minY - padding,
      w: width + padding * 2,
      h: height + padding * 2,
    };
  }

  return {
    x: minX,
    y: minY,
    w: width,
    h: height,
  };
};

const resolvePointsDrawingWorldRect = (
  points: Array<{ x: number; y: number }>,
  style: DrawingToolStyle,
): Rect => {
  const normalizedStyle = normalizeDrawingStyle(style);
  const fallbackPoint = points[0] ?? { x: 0, y: 0 };
  const minX = points.reduce((min, point) => Math.min(min, point.x), fallbackPoint.x);
  const minY = points.reduce((min, point) => Math.min(min, point.y), fallbackPoint.y);
  const maxX = points.reduce((max, point) => Math.max(max, point.x), fallbackPoint.x);
  const maxY = points.reduce((max, point) => Math.max(max, point.y), fallbackPoint.y);
  const padding = Math.max(8, normalizedStyle.strokeWidth * 3);
  return {
    x: minX - padding,
    y: minY - padding,
    w: Math.max(MIN_SHAPE_SIZE, maxX - minX) + padding * 2,
    h: Math.max(MIN_SHAPE_SIZE, maxY - minY) + padding * 2,
  };
};

const resolvePencilDrawingWorldRect = resolvePointsDrawingWorldRect;
const resolvePolygonDrawingWorldRect = resolvePointsDrawingWorldRect;

const getDrawingTextLetterSpacingPx = (style: DrawingToolStyle): number => (
  style.fontSize * ((style.letterSpacingPercent ?? 0) / 100)
);

const buildDrawingTextFont = (style: DrawingToolStyle): string => (
  `${style.textItalic ? 'italic' : 'normal'} ${style.fontWeight ?? (style.textBold ? 700 : 400)} ${style.fontSize}px ${style.fontFamily ?? DEFAULT_DRAWING_TEXT_FONT_FAMILY}`
);

const getTextAnchorX = (align: DrawingTextAlign | undefined, width: number, padding: number): number => {
  if (align === 'center') return width / 2;
  if (align === 'right') return Math.max(0, width - padding);
  return padding;
};

const getSvgTextAnchor = (align: DrawingTextAlign | undefined): string => {
  if (align === 'center') return 'middle';
  if (align === 'right') return 'end';
  return 'start';
};

const measureTextLineWidth = (
  ctx: CanvasRenderingContext2D,
  line: string,
  letterSpacingPx: number
): number => {
  const characters = Array.from(line || ' ');
  const charactersWidth = characters.reduce((sum, character) => sum + ctx.measureText(character).width, 0);
  const spacingWidth = letterSpacingPx * Math.max(0, characters.length - 1);
  return Math.max(1, charactersWidth + spacingWidth);
};

const buildStandaloneSvg = (width: number, height: number, children: string): string => [
  `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}">`,
  children,
  `</svg>`,
].join('\n');

const polylinePoints = (points: Array<{ x: number; y: number }>): string =>
  points.map((point) => `${Number(point.x.toFixed(3))},${Number(point.y.toFixed(3))}`).join(' ');

export const createPencilDrawingSvg = ({
  points,
  style,
}: PencilDrawingInput): DrawingSvgResult => {
  const normalizedStyle = normalizeDrawingStyle(style);
  const validPoints = points.filter((point) => (
    Number.isFinite(point.x) && Number.isFinite(point.y)
  ));
  if (validPoints.length === 0) {
    throw new Error('Pencil drawing requires at least one point.');
  }

  const worldRect = resolvePencilDrawingWorldRect(validPoints, normalizedStyle);
  const width = Math.max(1, Math.ceil(worldRect.w));
  const height = Math.max(1, Math.ceil(worldRect.h));
  const resolvedWorldRect = { ...worldRect, w: width, h: height };
  const localPoints = validPoints.map((point) => ({
    x: point.x - resolvedWorldRect.x,
    y: point.y - resolvedWorldRect.y,
  }));
  const stroke = escapeXmlAttribute(normalizedStyle.strokeColor);
  const strokeWidth = normalizedStyle.strokeWidth;
  const strokeOpacity = formatSvgNumber(normalizedStyle.strokeOpacity ?? 1);
  const children = localPoints.length === 1
    ? `<circle cx="${localPoints[0]?.x ?? 0}" cy="${localPoints[0]?.y ?? 0}" r="${Math.max(1, strokeWidth / 2)}" fill="${stroke}" fill-opacity="${strokeOpacity}"/>`
    : `<polyline points="${escapeXmlAttribute(polylinePoints(localPoints))}" fill="none" stroke="${stroke}" stroke-opacity="${strokeOpacity}" stroke-width="${strokeWidth}" stroke-linecap="round" stroke-linejoin="round"/>`;

  return {
    svg: buildStandaloneSvg(width, height, children),
    worldRect: resolvedWorldRect,
    payload: {
      kind: 'pencil',
      style: normalizedStyle,
      points: localPoints,
      createdAt: Date.now(),
      worldRect: resolvedWorldRect,
    },
  };
};

export const createPolygonDrawingSvg = ({
  points,
  style,
}: PolygonDrawingInput): DrawingSvgResult => {
  const normalizedStyle = normalizeDrawingStyle(style);
  const validPoints = points.filter((point) => (
    Number.isFinite(point.x) && Number.isFinite(point.y)
  ));
  if (validPoints.length === 0) {
    throw new Error('Polygon drawing requires at least one point.');
  }

  const worldRect = resolvePolygonDrawingWorldRect(validPoints, normalizedStyle);
  const width = Math.max(1, Math.ceil(worldRect.w));
  const height = Math.max(1, Math.ceil(worldRect.h));
  const resolvedWorldRect = { ...worldRect, w: width, h: height };
  const localPoints = validPoints.map((point) => ({
    x: point.x - resolvedWorldRect.x,
    y: point.y - resolvedWorldRect.y,
  }));
  const stroke = escapeXmlAttribute(normalizedStyle.strokeColor);
  const fill = normalizedStyle.fillEnabled ? escapeXmlAttribute(normalizedStyle.fillColor) : 'none';
  const strokeWidth = normalizedStyle.strokeWidth;
  const strokeOpacity = formatSvgNumber(normalizedStyle.strokeOpacity ?? 1);
  const fillOpacity = formatSvgNumber(normalizedStyle.fillOpacity ?? 1);
  const children = localPoints.length < 3
    ? `<polyline points="${escapeXmlAttribute(polylinePoints(localPoints))}" fill="none" stroke="${stroke}" stroke-opacity="${strokeOpacity}" stroke-width="${strokeWidth}" stroke-linecap="round" stroke-linejoin="round"/>`
    : `<polygon points="${escapeXmlAttribute(polylinePoints(localPoints))}" fill="${fill}" fill-opacity="${fillOpacity}" stroke="${stroke}" stroke-opacity="${strokeOpacity}" stroke-width="${strokeWidth}" stroke-linejoin="round" stroke-linecap="round"/>`;

  return {
    svg: buildStandaloneSvg(width, height, children),
    worldRect: resolvedWorldRect,
    payload: {
      kind: 'polygon',
      style: normalizedStyle,
      points: localPoints,
      createdAt: Date.now(),
      worldRect: resolvedWorldRect,
    },
  };
};

export const createShapeDrawingSvg = ({
  kind,
  start,
  end,
  style,
}: ShapeDrawingInput): DrawingSvgResult => {
  const normalizedStyle = normalizeDrawingStyle(style);
  const worldRect = resolveShapeDrawingWorldRect(start, end, kind, normalizedStyle);
  const width = Math.max(1, Math.ceil(worldRect.w));
  const height = Math.max(1, Math.ceil(worldRect.h));
  const resolvedWorldRect = { ...worldRect, w: width, h: height };
  const stroke = escapeXmlAttribute(normalizedStyle.strokeColor);
  const fill = normalizedStyle.fillEnabled ? escapeXmlAttribute(normalizedStyle.fillColor) : 'none';
  const strokeWidth = normalizedStyle.strokeWidth;
  const strokeOpacity = formatSvgNumber(normalizedStyle.strokeOpacity ?? 1);
  const fillOpacity = formatSvgNumber(normalizedStyle.fillOpacity ?? 1);
  const inset = strokeWidth / 2;
  let children = '';

  if (kind === 'rectangle') {
    children = `<rect x="${inset}" y="${inset}" width="${Math.max(1, width - strokeWidth)}" height="${Math.max(1, height - strokeWidth)}" rx="${Math.max(0, normalizedStyle.cornerRadius)}" fill="${fill}" fill-opacity="${fillOpacity}" stroke="${stroke}" stroke-opacity="${strokeOpacity}" stroke-width="${strokeWidth}"/>`;
  } else if (kind === 'ellipse') {
    children = `<ellipse cx="${width / 2}" cy="${height / 2}" rx="${Math.max(1, (width - strokeWidth) / 2)}" ry="${Math.max(1, (height - strokeWidth) / 2)}" fill="${fill}" fill-opacity="${fillOpacity}" stroke="${stroke}" stroke-opacity="${strokeOpacity}" stroke-width="${strokeWidth}"/>`;
  } else if (isPolygonalShapeKind(kind)) {
    const points = getPolygonalShapePoints(kind, width, height, inset);
    children = `<polygon points="${escapeXmlAttribute(polylinePoints(points))}" fill="${fill}" fill-opacity="${fillOpacity}" stroke="${stroke}" stroke-opacity="${strokeOpacity}" stroke-width="${strokeWidth}" stroke-linejoin="round" stroke-linecap="round"/>`;
  } else {
    const localStart = { x: start.x - worldRect.x, y: start.y - worldRect.y };
    const localEnd = { x: end.x - worldRect.x, y: end.y - worldRect.y };
    const line = `<line x1="${localStart.x}" y1="${localStart.y}" x2="${localEnd.x}" y2="${localEnd.y}" stroke="${stroke}" stroke-opacity="${strokeOpacity}" stroke-width="${strokeWidth}" stroke-linecap="round"/>`;
    if (kind === 'arrow') {
      const angle = Math.atan2(localEnd.y - localStart.y, localEnd.x - localStart.x);
      const headLength = Math.max(12, strokeWidth * 4);
      const headOne = {
        x: localEnd.x - headLength * Math.cos(angle - Math.PI / 7),
        y: localEnd.y - headLength * Math.sin(angle - Math.PI / 7),
      };
      const headTwo = {
        x: localEnd.x - headLength * Math.cos(angle + Math.PI / 7),
        y: localEnd.y - headLength * Math.sin(angle + Math.PI / 7),
      };
      children = [
        line,
        `<path d="M ${localEnd.x} ${localEnd.y} L ${headOne.x} ${headOne.y} M ${localEnd.x} ${localEnd.y} L ${headTwo.x} ${headTwo.y}" fill="none" stroke="${stroke}" stroke-opacity="${strokeOpacity}" stroke-width="${strokeWidth}" stroke-linecap="round" stroke-linejoin="round"/>`,
      ].join('\n');
    } else {
      children = line;
    }
  }

  return {
    svg: buildStandaloneSvg(width, height, children),
    worldRect: resolvedWorldRect,
    payload: {
      kind,
      style: normalizedStyle,
      createdAt: Date.now(),
      worldRect: resolvedWorldRect,
    },
  };
};

export const createTextDrawingSvg = ({
  text,
  anchor,
  style,
}: TextDrawingInput): DrawingSvgResult => {
  const normalizedStyle = normalizeDrawingStyle(style);
  const lines = text.replace(/\r/g, '').split('\n');
  const sanitizedLines = lines.length > 0 ? lines : [''];
  const fontWeight = normalizedStyle.fontWeight ?? (normalizedStyle.textBold ? 700 : 400);
  const fontStyle = normalizedStyle.textItalic ? 'italic' : 'normal';
  const fontFamily = normalizedStyle.fontFamily ?? DEFAULT_DRAWING_TEXT_FONT_FAMILY;
  const font = buildDrawingTextFont(normalizedStyle);
  const measureCanvas = document.createElement('canvas');
  const measureCtx = measureCanvas.getContext('2d');
  if (!measureCtx) {
    throw new Error('Text drawing canvas is unavailable.');
  }
  measureCtx.font = font;
  const lineHeight = Math.max(1, Math.round(normalizedStyle.fontSize * (normalizedStyle.lineHeight ?? DEFAULT_TEXT_LINE_HEIGHT)));
  const padding = Math.max(4, Math.round(normalizedStyle.fontSize * TEXT_PADDING_FACTOR));
  const letterSpacingPx = getDrawingTextLetterSpacingPx(normalizedStyle);
  const measuredWidth = sanitizedLines.reduce((max, line) => (
    Math.max(max, measureTextLineWidth(measureCtx, line || ' ', letterSpacingPx))
  ), 1);
  const width = Math.max(1, Math.ceil(measuredWidth + padding * 2));
  const height = Math.max(1, Math.ceil(sanitizedLines.length * lineHeight + padding * 2));
  const fill = escapeXmlAttribute(normalizedStyle.fontColor);
  const fontOpacity = formatSvgNumber(normalizedStyle.fontOpacity ?? 1);
  const textDecoration = normalizedStyle.textUnderline ? 'underline' : 'none';
  const textAnchor = getSvgTextAnchor(normalizedStyle.textAlign);
  const textNodes = sanitizedLines.map((line, index) => {
    const renderedLine = line || ' ';
    const x = getTextAnchorX(normalizedStyle.textAlign, width, padding);
    const y = padding + index * lineHeight;
    return `<text x="${formatSvgNumber(x)}" y="${formatSvgNumber(y)}" fill="${fill}" fill-opacity="${fontOpacity}" font-family="${escapeXmlAttribute(fontFamily)}" font-size="${normalizedStyle.fontSize}" font-weight="${fontWeight}" font-style="${fontStyle}" text-decoration="${textDecoration}" text-anchor="${textAnchor}" letter-spacing="${formatSvgNumber(letterSpacingPx)}" dominant-baseline="text-before-edge">${escapeSvgText(renderedLine)}</text>`;
  }).join('\n');
  const worldRect = {
    x: anchor.x,
    y: anchor.y,
    w: width,
    h: height,
  };

  return {
    svg: buildStandaloneSvg(width, height, textNodes),
    worldRect,
    payload: {
      kind: 'text',
      style: normalizedStyle,
      text,
      createdAt: Date.now(),
      worldRect,
    },
  };
};

const resolvePayloadSvgDimensions = (payload: DrawingLayerPayload): { width: number; height: number } => ({
  width: Math.max(1, Math.ceil(readFiniteNumber(payload.worldRect?.w, 1))),
  height: Math.max(1, Math.ceil(readFiniteNumber(payload.worldRect?.h, 1))),
});

const normalizePayloadPoints = (
  points: Array<{ x: number; y: number }> | undefined,
  payload: DrawingLayerPayload,
  width: number,
  height: number,
): Array<{ x: number; y: number }> => {
  const validPoints = (points ?? []).filter((point) => (
    Number.isFinite(point.x) && Number.isFinite(point.y)
  ));
  if (validPoints.length === 0) return [];

  const padding = Math.max(8, normalizeDrawingStyle(payload.style).strokeWidth * 3);
  const areProbablyLocal = validPoints.every((point) => (
    point.x >= -padding &&
    point.y >= -padding &&
    point.x <= width + padding &&
    point.y <= height + padding
  ));
  if (areProbablyLocal) return validPoints;

  const rect = payload.worldRect;
  return validPoints.map((point) => ({
    x: point.x - rect.x,
    y: point.y - rect.y,
  }));
};

const buildDrawingPayloadSvgChildren = (
  payload: DrawingLayerPayload,
  width: number,
  height: number,
): string => {
  const normalizedStyle = normalizeDrawingStyle(payload.style);
  const stroke = escapeXmlAttribute(normalizedStyle.strokeColor);
  const fill = normalizedStyle.fillEnabled ? escapeXmlAttribute(normalizedStyle.fillColor) : 'none';
  const strokeWidth = normalizedStyle.strokeWidth;
  const strokeOpacity = formatSvgNumber(normalizedStyle.strokeOpacity ?? 1);
  const fillOpacity = formatSvgNumber(normalizedStyle.fillOpacity ?? 1);
  const inset = strokeWidth / 2;

  if (payload.kind === 'rectangle') {
    return `<rect x="${formatSvgNumber(inset)}" y="${formatSvgNumber(inset)}" width="${formatSvgNumber(Math.max(1, width - strokeWidth))}" height="${formatSvgNumber(Math.max(1, height - strokeWidth))}" rx="${formatSvgNumber(Math.max(0, normalizedStyle.cornerRadius))}" fill="${fill}" fill-opacity="${fillOpacity}" stroke="${stroke}" stroke-opacity="${strokeOpacity}" stroke-width="${strokeWidth}"/>`;
  }

  if (payload.kind === 'ellipse') {
    return `<ellipse cx="${formatSvgNumber(width / 2)}" cy="${formatSvgNumber(height / 2)}" rx="${formatSvgNumber(Math.max(1, (width - strokeWidth) / 2))}" ry="${formatSvgNumber(Math.max(1, (height - strokeWidth) / 2))}" fill="${fill}" fill-opacity="${fillOpacity}" stroke="${stroke}" stroke-opacity="${strokeOpacity}" stroke-width="${strokeWidth}"/>`;
  }

  if (isPolygonalShapeKind(payload.kind)) {
    const points = getPolygonalShapePoints(payload.kind, width, height, inset);
    return `<polygon points="${escapeXmlAttribute(polylinePoints(points))}" fill="${fill}" fill-opacity="${fillOpacity}" stroke="${stroke}" stroke-opacity="${strokeOpacity}" stroke-width="${strokeWidth}" stroke-linejoin="round" stroke-linecap="round"/>`;
  }

  if (payload.kind === 'pencil') {
    const points = normalizePayloadPoints(payload.points, payload, width, height);
    if (points.length === 0) return '';
    return points.length === 1
      ? `<circle cx="${formatSvgNumber(points[0]?.x ?? 0)}" cy="${formatSvgNumber(points[0]?.y ?? 0)}" r="${formatSvgNumber(Math.max(1, strokeWidth / 2))}" fill="${stroke}" fill-opacity="${strokeOpacity}"/>`
      : `<polyline points="${escapeXmlAttribute(polylinePoints(points))}" fill="none" stroke="${stroke}" stroke-opacity="${strokeOpacity}" stroke-width="${strokeWidth}" stroke-linecap="round" stroke-linejoin="round"/>`;
  }

  if (payload.kind === 'polygon') {
    const points = normalizePayloadPoints(payload.points, payload, width, height);
    if (points.length === 0) return '';
    return points.length < 3
      ? `<polyline points="${escapeXmlAttribute(polylinePoints(points))}" fill="none" stroke="${stroke}" stroke-opacity="${strokeOpacity}" stroke-width="${strokeWidth}" stroke-linecap="round" stroke-linejoin="round"/>`
      : `<polygon points="${escapeXmlAttribute(polylinePoints(points))}" fill="${fill}" fill-opacity="${fillOpacity}" stroke="${stroke}" stroke-opacity="${strokeOpacity}" stroke-width="${strokeWidth}" stroke-linejoin="round" stroke-linecap="round"/>`;
  }

  if (payload.kind === 'text') {
    const text = typeof payload.text === 'string' ? payload.text : '';
    const lines = text.replace(/\r/g, '').split('\n');
    const sanitizedLines = lines.length > 0 ? lines : [''];
    const fontWeight = normalizedStyle.fontWeight ?? (normalizedStyle.textBold ? 700 : 400);
    const fontStyle = normalizedStyle.textItalic ? 'italic' : 'normal';
    const fontFamily = normalizedStyle.fontFamily ?? DEFAULT_DRAWING_TEXT_FONT_FAMILY;
    const lineHeight = Math.max(1, Math.round(normalizedStyle.fontSize * (normalizedStyle.lineHeight ?? DEFAULT_TEXT_LINE_HEIGHT)));
    const padding = Math.max(4, Math.round(normalizedStyle.fontSize * TEXT_PADDING_FACTOR));
    const letterSpacingPx = getDrawingTextLetterSpacingPx(normalizedStyle);
    const fillColor = escapeXmlAttribute(normalizedStyle.fontColor);
    const fontOpacity = formatSvgNumber(normalizedStyle.fontOpacity ?? 1);
    const textDecoration = normalizedStyle.textUnderline ? 'underline' : 'none';
    const textAnchor = getSvgTextAnchor(normalizedStyle.textAlign);
    return sanitizedLines.map((line, index) => {
      const renderedLine = line || ' ';
      const x = Math.min(Math.max(0, getTextAnchorX(normalizedStyle.textAlign, width, padding)), Math.max(0, width - 1));
      const y = Math.min(Math.max(0, padding + index * lineHeight), Math.max(0, height - 1));
      return `<text x="${formatSvgNumber(x)}" y="${formatSvgNumber(y)}" fill="${fillColor}" fill-opacity="${fontOpacity}" font-family="${escapeXmlAttribute(fontFamily)}" font-size="${normalizedStyle.fontSize}" font-weight="${fontWeight}" font-style="${fontStyle}" text-decoration="${textDecoration}" text-anchor="${textAnchor}" letter-spacing="${formatSvgNumber(letterSpacingPx)}" dominant-baseline="text-before-edge">${escapeSvgText(renderedLine)}</text>`;
    }).join('\n');
  }

  const points = normalizePayloadPoints(payload.points, payload, width, height);
  const fallbackPadding = Math.max(8, strokeWidth * 3);
  const localStart = points[0] ?? { x: fallbackPadding, y: fallbackPadding };
  const localEnd = points[1] ?? {
    x: Math.max(fallbackPadding, width - fallbackPadding),
    y: Math.max(fallbackPadding, height - fallbackPadding),
  };
  const line = `<line x1="${formatSvgNumber(localStart.x)}" y1="${formatSvgNumber(localStart.y)}" x2="${formatSvgNumber(localEnd.x)}" y2="${formatSvgNumber(localEnd.y)}" stroke="${stroke}" stroke-opacity="${strokeOpacity}" stroke-width="${strokeWidth}" stroke-linecap="round"/>`;
  if (payload.kind !== 'arrow') return line;

  const angle = Math.atan2(localEnd.y - localStart.y, localEnd.x - localStart.x);
  const headLength = Math.max(12, strokeWidth * 4);
  const headOne = {
    x: localEnd.x - headLength * Math.cos(angle - Math.PI / 7),
    y: localEnd.y - headLength * Math.sin(angle - Math.PI / 7),
  };
  const headTwo = {
    x: localEnd.x - headLength * Math.cos(angle + Math.PI / 7),
    y: localEnd.y - headLength * Math.sin(angle + Math.PI / 7),
  };
  return [
    line,
    `<path d="M ${formatSvgNumber(localEnd.x)} ${formatSvgNumber(localEnd.y)} L ${formatSvgNumber(headOne.x)} ${formatSvgNumber(headOne.y)} M ${formatSvgNumber(localEnd.x)} ${formatSvgNumber(localEnd.y)} L ${formatSvgNumber(headTwo.x)} ${formatSvgNumber(headTwo.y)}" fill="none" stroke="${stroke}" stroke-opacity="${strokeOpacity}" stroke-width="${strokeWidth}" stroke-linecap="round" stroke-linejoin="round"/>`,
  ].join('\n');
};

export const getDrawingLayerSvgSource = (params: unknown): DrawingLayerSvgSource | null => {
  const payload = getDrawingLayerPayload(params);
  if (!payload) return null;
  const { width, height } = resolvePayloadSvgDimensions(payload);
  const viewBox = `0 0 ${width} ${height}`;
  const children = buildDrawingPayloadSvgChildren(payload, width, height);
  const svg = buildStandaloneSvg(width, height, children);
  return {
    svg,
    source: svgMarkupToDataUrl(svg),
    width,
    height,
    viewBox,
  };
};

export const getDrawingAwareSvgLayerSource = (
  layer: { imageSrc?: string | null; params?: unknown } | null | undefined
): string | null => {
  if (!layer) return null;
  const svgPayload = getSvgLayerPayload(layer.params);
  if (svgPayload) {
    if (svgPayload.svg) return svgMarkupToDataUrl(svgPayload.svg);
    if (svgPayload.url) return svgPayload.url;
    const imageSrc = typeof layer.imageSrc === 'string' ? layer.imageSrc.trim() : '';
    return imageSrc.length > 0 ? imageSrc : (getDrawingLayerSvgSource(layer.params)?.source ?? null);
  }
  const drawingSource = getDrawingLayerSvgSource(layer.params);
  if (drawingSource) return drawingSource.source;
  const imageSrc = typeof layer.imageSrc === 'string' ? layer.imageSrc.trim() : '';
  return imageSrc.length > 0 ? imageSrc : null;
};
